import { gql } from "@apollo/client";
import HarvestFormFields, { HarvestFormType } from "components/entities/agriculture/work/HarvestFormFields";
import { ComponentStateTypes } from "components/common/ComponentState";
import Page from "components/common/page/Page";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { HarvestTemplateFormDataQuery, HarvestTemplateFormDataQueryVariables, SaveHarvestTemplateMutation, SaveHarvestTemplateMutationVariables, HarvestAgricultureWorkTemplateInput } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";
import { getHarvestFormData, getHarvestSaveForm } from "utilities/entities/agriculture/worklogs/work-form-utilities";

const workFormQuery = gql`
query HarvestTemplateFormData($id: Int) {
  harvestAgricultureWorkTemplate(id: $id) {
      id
      season {
        id
        name
        active
      }
      startDate
      note
      name
      workers {
        items {
          person {
            id
            name
          }
          id
          price
        }
      }
      harvests {
        items {
          id
          kg
          cartQuantity
          hideFromMultiUpdate
          fruit {
            id
            name
          }
          harvestFruitGroups {
            items {
              id
              fruitGroup {
                id
                name
              }
            }
          }
          harvestFruitCategories {
            items {
              id
              fruitCategory {
                id
                name
              }
            }
          }
        }
      }
    }
  }  
`;

const saveFormMutation = gql`
mutation SaveHarvestTemplate($model: HarvestAgricultureWorkTemplateInput!) {
    harvestAgricultureWorkTemplate(model: $model) {
        id
    }
}
`;


const WorkTemplateFormPage = () => {
    const params = useParams<{id?: string}>();
    const context = useAppContext();
    const [t] = useTranslation();
    const navigate = useNavigate();
    const [componentState, setComponentState] = useState<ComponentStateTypes>();
    const [model, setModel] = useState<HarvestFormType>();

    

    // #region load data
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<HarvestTemplateFormDataQuery, HarvestTemplateFormDataQueryVariables>({
                query: workFormQuery,
                variables: {
                    id: Number(params.id) || 0
                }
            });

            if (response.errors) {
                context.notify('error', t('errors.loading-data'));
                setComponentState('error');
                throw response.errors;
            }

            if(response.data?.harvestAgricultureWorkTemplate) {
                setModel(getHarvestFormData(response.data?.harvestAgricultureWorkTemplate, true));
            }
            setComponentState('')
        } catch (error) {
            context.notify('error', t('errors.loading-data'));
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load data

    const onSubmit = async (model: HarvestFormType) => {
        let saveModel = getHarvestSaveForm<HarvestAgricultureWorkTemplateInput>(model);

        try {
            const response = await graph<SaveHarvestTemplateMutation, SaveHarvestTemplateMutationVariables>({
                query: saveFormMutation,
                variables: {
                    model: saveModel
                }
            });
    
            if(response.errors) {
                throw response.errors
            }

            navigate("/app/agriculture/overview/other");
        } catch (error: any) {
            context.notify('error', 'form.submit.error', Array.isArray(error) ? error[0].message : error.message);
            throw error
        }
    }
    
    return (
        <Page title="pages.template.work.form" state={componentState}>
            <HarvestFormFields
                model={model}
                name="harvest-template-form"
                onSubmit={onSubmit}
                isTemplate
            />
        </Page>
    );
};

export default WorkTemplateFormPage;
